import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Button, Input, MoneyInput } from '../../components/v2/V2Components';
import BulkImportPanel from '../../components/v2/BulkImportPanel';
import MortgageService from '../../services/MortgageService';
import { toDateInputValue } from '../../utils/billFormat';
import { formatMoney, toSubmittableMoney } from '../../utils/numericInput';

const LABEL_CLASS = 'block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1';

// Money fields the fix card shows for correction, in reading order.
const MONEY_FIELDS = [
  ['total_amount_due', 'Total Due', { required: true }],
  ['principal', 'Principal'],
  ['interest', 'Interest'],
  ['escrow', 'Escrow'],
  ['principal_balance', 'Principal Balance'],
  ['escrow_balance', 'Escrow Balance', { allowNegative: true }],
];
// Extracted fields the card doesn't show; carried through on save so fixing
// the due date doesn't silently drop them. Only values the server will accept
// are carried: the user has no input to correct one it would reject.
const CARRIED_MONEY_FIELDS = ['fees', 'additional_payment', 'past_due_amount'];

const isNumeric = (value) => value !== '' && Number.isFinite(Number(value));
// interest_rate is a percent with up to 3 decimals, 0–100.
const submittableRate = (value) => {
  const rate = Number(value);
  return value != null && value !== '' && Number.isFinite(rate) && rate >= 0 && rate <= 100
    ? Math.round(rate * 1000) / 1000
    : null;
};

/**
 * One statement the import couldn't create, editable in place. The File is
 * still in memory, so saving attaches the right PDF through the ordinary
 * create endpoint (validation, R2 upload and the duplicate 409 included).
 */
const StatementFixCard = ({ item, mortgageId, onSaved, onSkip }) => {
  const fields = item.fields || {};
  const [values, setValues] = useState(() => ({
    due_date: toDateInputValue(fields.due_date),
    statement_date: toDateInputValue(fields.statement_date),
    // Formatted like MoneyInput displays it, so what is saved is what is shown.
    ...Object.fromEntries(MONEY_FIELDS.map(([key]) => [key, formatMoney(fields[key])])),
  }));
  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState(null);

  const fieldId = (key) => `statement-fix-${item.clientId}-${key}`;
  const setField = (key) => (e) => setValues((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!values.due_date || !values.total_amount_due) {
      setInlineError('Due date and total due are required');
      return;
    }
    const badMoney = MONEY_FIELDS.find(([key]) => values[key] !== '' && !isNumeric(values[key]));
    if (badMoney) {
      setInlineError(`${badMoney[1]} must be a number`);
      return;
    }

    const carried = Object.fromEntries(
      [
        ...CARRIED_MONEY_FIELDS.map((key) => [key, toSubmittableMoney(fields[key])]),
        ['interest_rate', submittableRate(fields.interest_rate)],
      ].filter(([, value]) => value != null),
    );
    try {
      setSaving(true);
      setInlineError(null);
      // createStatement drops blank values, so an empty optional field is
      // stored as NULL rather than rejected.
      const created = await MortgageService.createStatement(mortgageId, {
        ...carried,
        ...values,
        file: item.file,
      });
      onSaved(item.clientId, created);
    } catch (err) {
      setInlineError(err.message || 'Could not save this statement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      aria-label={`Fix ${item.filename}`}
      onSubmit={handleSave}
      className="p-4 rounded-xl border border-[var(--border)] bg-[var(--bg-hover)] mb-3"
    >
      <div className="flex items-start gap-2 mb-3 min-w-0">
        <AlertCircle size={16} className="text-[var(--error)] shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[var(--text-main)] truncate">{item.filename}</p>
          <p className="text-xs text-[var(--text-secondary)]">{item.error}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor={fieldId('due_date')} className={LABEL_CLASS}>
            Payment Due Date *
          </label>
          <Input id={fieldId('due_date')} type="date" value={values.due_date} onChange={setField('due_date')} />
        </div>
        <div>
          <label htmlFor={fieldId('statement_date')} className={LABEL_CLASS}>
            Statement Date
          </label>
          <Input
            id={fieldId('statement_date')}
            type="date"
            value={values.statement_date}
            onChange={setField('statement_date')}
          />
        </div>
        {MONEY_FIELDS.map(([key, label, opts = {}]) => (
          <div key={key}>
            <label htmlFor={fieldId(key)} className={LABEL_CLASS}>
              {opts.required ? `${label} *` : label}
            </label>
            <MoneyInput
              id={fieldId(key)}
              name={key}
              value={values[key]}
              allowNegative={Boolean(opts.allowNegative)}
              onChange={setField(key)}
            />
          </div>
        ))}
      </div>

      {inlineError && (
        <p className="text-xs text-[var(--error)] mt-3" role="alert">
          {inlineError}
        </p>
      )}

      <div className="flex flex-col sm:flex-row sm:justify-end gap-2 mt-4">
        <Button type="button" variant="ghost" className="w-full sm:w-auto" onClick={() => onSkip(item.clientId)}>
          Skip
        </Button>
        <Button type="submit" loading={saving} className="w-full sm:w-auto">
          Save statement
        </Button>
      </div>
    </form>
  );
};

/**
 * Bulk mortgage statement import (ADR-0012): drop a folder of statements,
 * come back to rows. The runner, queue and summary are shared with the bill
 * import (BulkImportPanel); this supplies the statement request and fix card.
 * What's still missing shows in the page's statement coverage grid.
 */
const BulkStatementImport = ({ mortgageId, onStatementsCreated, onClose }) => (
  <BulkImportPanel
    title="Bulk Statements"
    noun={{ one: 'statement', many: 'statements' }}
    dropTitle="Drop your statement PDFs here"
    dropHint="One file per statement. We read each one and add it for you — you only get asked about files we can't read."
    inputTestId="bulk-statement-file-picker-input"
    unavailableMessage="Statement scanning is unavailable right now — try again in a few minutes"
    importFile={(file, opts) => MortgageService.importStatement(mortgageId, file, opts)}
    recordOf={(result) => result.statement}
    onRecordsCreated={onStatementsCreated}
    renderFixCard={({ item, onSaved, onSkip }) => (
      <StatementFixCard item={item} mortgageId={mortgageId} onSaved={onSaved} onSkip={onSkip} />
    )}
    onClose={onClose}
  />
);

export default BulkStatementImport;
